/**
 * Phase 5 — Executive Targets Management
 * Set target per metric per periode. Permission: EXEC_TARGET_UBAH (lihat: EXEC_TARGET_LIHAT).
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Save } from "lucide-react";
import { formatRupiah } from "@/lib/format";

export default function ExecutiveTargets() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [err, setErr] = useState(null);
  const [saving, setSaving] = useState(null);
  const user = JSON.parse(localStorage.getItem("abs_user") || "null") || {};
  const canEdit = ["super_admin", "direktur_utama", "direktur_operasional"].includes(user.role);
  const isDirops = user.role === "direktur_operasional";
  const diropScope = new Set(["inventory_value", "overdue_ar_percent", "overdue_ap_percent"]);

  const load = () => {
    setErr(null);
    api.get(`/executive/targets?year=${year}&month=${month}`)
      .then(({ data }) => {
        setMeta(data.metric_meta || {});
        // Compose full list from metric_meta, filling target from items when present
        const byMetric = {};
        for (const i of data.items) byMetric[i.metric] = i;
        const rows = Object.entries(data.metric_meta || {}).map(([metric, m]) => ({
          metric, label: m.label, unit: m.unit, direction: m.direction,
          target: byMetric[metric]?.target ?? "",
          owner: byMetric[metric]?.owner ?? "",
          updated_at: byMetric[metric]?.updated_at ?? null,
          updated_by: byMetric[metric]?.updated_by ?? null,
          _dirty: false,
        }));
        setItems(rows);
      })
      .catch((e) => {
        if (e?.response?.status === 403) setErr("Tidak berwenang mengakses target eksekutif.");
        else setErr(formatApiError(e));
      });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const update = (metric, field, value) => {
    setItems((prev) => prev.map((r) => r.metric === metric ? { ...r, [field]: value, _dirty: true } : r));
  };

  const save = async (row) => {
    setSaving(row.metric);
    try {
      const payload = {
        year, month, metric: row.metric,
        target: Number(row.target) || 0,
        owner: row.owner || null,
        updated_at_prev: row.updated_at,
      };
      const { data } = await api.put(`/executive/targets/${row.metric}`, payload);
      setItems((prev) => prev.map((r) => r.metric === row.metric
        ? { ...r, target: data.target, owner: data.owner, updated_at: data.updated_at, updated_by: data.updated_by, _dirty: false }
        : r));
      toast.success(`Target ${row.label} disimpan.`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(null); }
  };

  if (err) {
    return (
      <div className="p-6 space-y-4" data-testid="exec-targets-page">
        <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
          <ArrowLeft size={12} className="mr-1" />Kembali
        </Link>
        <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-900 text-sm" data-testid="exec-targets-no-access">
          {err}
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="exec-targets-page">
      <Link to="/executive-dashboard" className="text-xs text-sky-700 hover:underline inline-flex items-center">
        <ArrowLeft size={12} className="mr-1" />Kembali ke Dashboard Eksekutif
      </Link>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Target Eksekutif</h1>
          <p className="text-xs md:text-sm text-slate-500">
            Set target bulanan per metric. {canEdit ? "" : "Mode read-only."}
            {isDirops && " Direktur Operasional hanya dapat mengubah target operasional (inventory, overdue AR/AP)."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs">Tahun</Label>
          <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} className="w-24" data-testid="target-year" />
          <Label className="text-xs">Bulan</Label>
          <Input type="number" min="1" max="12" value={month} onChange={(e) => setMonth(Math.max(1, Math.min(12, Number(e.target.value) || month)))} className="w-20" data-testid="target-month" />
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Daftar Target {year}-{String(month).padStart(2, "0")}</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Metric</TableHead>
              <TableHead>Arah</TableHead>
              <TableHead className="text-right">Target</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Owner</TableHead>
              <TableHead>Diubah</TableHead>
              <TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((r) => {
                const metricDisabled = !canEdit || (isDirops && !diropScope.has(r.metric));
                return (
                  <TableRow key={r.metric} data-testid={`target-row-${r.metric}`}>
                    <TableCell>{r.label}</TableCell>
                    <TableCell><Badge variant="outline" className="text-[10px]">{r.direction}</Badge></TableCell>
                    <TableCell className="text-right">
                      <Input type="number" step="any" value={r.target}
                               disabled={metricDisabled}
                               onChange={(e) => update(r.metric, "target", e.target.value)}
                               className="w-32 ml-auto text-right"
                               data-testid={`target-value-${r.metric}`} />
                    </TableCell>
                    <TableCell className="text-xs">{r.unit}</TableCell>
                    <TableCell>
                      <Input type="text" value={r.owner || ""}
                               disabled={metricDisabled}
                               onChange={(e) => update(r.metric, "owner", e.target.value)}
                               placeholder="Direktur …"
                               className="w-40"
                               data-testid={`target-owner-${r.metric}`} />
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.updated_at ? new Date(r.updated_at).toLocaleDateString("id-ID") : "—"}
                      <div className="text-[10px]">oleh {r.updated_by || "—"}</div>
                    </TableCell>
                    <TableCell>
                      {!metricDisabled && (
                        <Button variant="outline" size="sm"
                                  disabled={!r._dirty || saving === r.metric}
                                  onClick={() => save(r)}
                                  data-testid={`target-save-${r.metric}`}>
                          <Save size={12} className="mr-1" />Simpan
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
